import { useMemo } from 'react'
import isBefore from 'date-fns/isBefore'
import { MEDICAL_HISTORY_CATEGORY_NAME } from 'Data/common-constants'
import { equals } from 'Data/aggregate.js'
import { useDataValue } from 'Simple/Data.js'
import { dateISOToMMMMdoYYYY, personName } from 'Data/format.js'
import parseISO from 'date-fns/parseISO'

function parseDate(rvalue) {
  if (!rvalue) return null
  return rvalue instanceof Date ? rvalue : parseISO(rvalue)
}

let CATEGORY_ORDER = {
  general: 0,
  medical_conditions: 1,
  habits: 2,
  medications: 3,
  drugs_allergies_and_sensitivities: 4,
}

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  let patient = useDataValue({
    viewPath: props.viewPath,
    context: 'patient',
    path: 'person',
  })

  return useMemo(() => {
    if (!data) return data

    let lastAnsweredForm = getLastAnsweredFormIndex(data.forms)

    return data.forms.slice(0, lastAnsweredForm + 1).map(form => {
      let { id, active, created_at, questions } = form

      // only add the internal questions for the last active form
      if (form.active && data.internal_questions?.length) {
        questions = [...questions, ...data.internal_questions].sort(
          (a, b) => a.order - b.order
        )
      }
      return {
        id,
        active,
        created_at,
        name: dateISOToMMMMdoYYYY(created_at),
        categories: formatCategories(questions, patient, active).sort(
          (a, b) => CATEGORY_ORDER[a.id] - CATEGORY_ORDER[b.id]
        ),
        last_modified_by: getLastModifiedBy(questions),
      }
    })
  }, [data, patient])
}

export function getLastModifiedBy(questions) {
  let lastAnswer =
    Array.isArray(questions) && questions.length !== 0
      ? questions
          .map(question => question.answers?.[0])
          .filter(Boolean)
          .reduce((lastAnswer, answer) => {
            let date = parseDate(answer.created_at)
            if (
              (!lastAnswer && date) ||
              lastAnswer?.last_modified?.getTime() < date.getTime()
            ) {
              return {
                user: answer.user?.vaxiom_person
                  ? personName(answer.user?.vaxiom_person)
                  : 'None',
                last_modified: date,
              }
            }
            return lastAnswer
          }, undefined)
      : undefined

  return lastAnswer?.user ?? 'None'
}

function getLastAnsweredFormIndex(forms) {
  let lastIndex = forms.findLastIndex(form => {
    return form.questions
      .flatMap(q => q.answers)
      .some(answer =>
        isBefore(new Date(answer.created_at), new Date(form.created_at))
      )
  })

  return lastIndex + 1
}

function formatCategories(questions, patient, active) {
  let questionByCat = questions.reduce((acc, question) => {
    if (!acc[question.category]) {
      acc[question.category] = []
    }

    acc[question.category].push(question)

    return acc
  }, {})

  return Object.entries(questionByCat).map(([key, value]) => ({
    id: key,
    category: key,
    questions: formatQuestions(value, patient),
    disabled: !active,
  }))
}

export function formatQuestions(questions, patient) {
  let formattedQuestions = questions.flatMap(({ answers, ...rest }) => {
    let question_type = rest.type
    let answer = getAnswerValue(answers, question_type)
    let created_at = Array.isArray(answers) ? answers[0]?.created_at : null
    let user = Array.isArray(answers)
      ? getCreatedBy(answers[0]?.user?.vaxiom_person ?? patient)
      : null
    let alert = Array.isArray(answers) ? answers[0]?.alert : null
    if (
      MEDICAL_HISTORY_CATEGORY_NAME.habits === rest.name ||
      MEDICAL_HISTORY_CATEGORY_NAME.medical_conditions === rest.name ||
      MEDICAL_HISTORY_CATEGORY_NAME.substances === rest.name
    ) {
      let options =
        typeof rest.options === 'string'
          ? rest.options.split(',').filter(Boolean)
          : rest.options

      if (!Array.isArray(options)) return []
      return options.map((option, idx) => {
        return {
          ...rest,
          parent_id: rest.id,
          parent_type: rest.type,
          parent_name: rest.name,
          parent_answer: answer,
          parent_alert: alert,
          id: rest.id + idx,
          name: option,
          type: 'boolean',
          answer: !!answer?.includes(option),
          original_answer: !!answer?.includes(option),
          alert: getAlertValueForSubtype(rest, option, alert),
          original_alert: getAlertValueForSubtype(rest, option, alert),
          created_at: created_at ? new Date(created_at) : null,
          user,
          historical_data: getHistoricalData({
            answers,
            patient,
            option,
          }),
        }
      })
    }

    return {
      ...rest,
      type: /(Chief concerns|Additional information)/.test(rest.name)
        ? 'textMultiLine'
        : rest.type,
      answer,
      original_answer: answer,
      alert: getAlertValue(rest, answer, alert),
      original_alert: getAlertValue(rest, answer, alert),
      created_at: created_at ? new Date(created_at) : null,
      user,
      historical_data: getHistoricalData({ answers, patient }),
    }
  })

  formattedQuestions.sort((a, b) => {
    if (a.type === 'multichoice' && b.type !== 'multichoice') {
      return 1
    }

    if (a.type !== 'multichoice' && b.type === 'multichoice') {
      return -1
    }

    return 0
  })

  return formattedQuestions
}

function getAnswerValue(answers, question_type) {
  if (Array.isArray(answers)) {
    let answer = answers[0]?.value

    if (answer !== null && answer !== undefined) {
      return answer
    } else if (['multichoice', 'list'].includes(question_type)) {
      return []
    }
  }

  return null
}

function getAlertValue({ alert_type, alert_conditions }, answer, alert) {
  if (alert !== undefined && alert !== null) {
    return alert
  }
  switch (alert_type) {
    case 'any':
      return true
    case 'values':
      return Array.isArray(alert_conditions)
        ? alert_conditions.includes(answer)
        : false
    default:
      return false
  }
}

function getAlertValueForSubtype(
  { alert_type, alert_conditions },
  answer,
  alert
) {
  if ([true, false].includes(alert)) {
    return alert
  } else if (Array.isArray(alert)) {
    return alert.includes(answer)
  }
  switch (alert_type) {
    case 'any':
      return true
    case 'values':
      return Array.isArray(alert)
        ? alert.includes(answer)
        : Array.isArray(alert_conditions)
        ? alert_conditions.includes(answer)
        : false
    default:
      return false
  }
}

function getCreatedBy(person) {
  return `${person.first_name} ${person.last_name}`
}

function getHistoricalData({ answers, patient, option }) {
  return answers
    .filter((answer, index, array) => {
      let prevAnswerValue = index !== 0 ? array[index - 1].value : null
      let answerValue = answer.value
      return (
        (prevAnswerValue === null || !equals(prevAnswerValue, answerValue)) &&
        answerValue !== null &&
        answerValue !== undefined &&
        answer.created_at !== null &&
        answer.created_at !== undefined
      )
    })
    .map(answer => {
      let answerValue = answer.value
      return {
        answer: option ? answerValue.includes(option) : answerValue,
        created_at: new Date(answer?.created_at),
        user: getCreatedBy(answer?.user?.vaxiom_person ?? patient),
      }
    })
}
